import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { ExclamationTriangleIcon, ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import UarCatalogEditor from '../../../components/mobile/UarCatalogEditor'

/** Tenant workspace tab: configure UAR types and options for PaySafeMobile floor tablets. */
export default function TenantUarConfigTab({ groupId, venues: venuesProp = [] }) {
  const apiBase = useMemo(
    () => getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(groupId)}/payout-mobile`),
    [groupId]
  )
  const venues = useMemo(
    () =>
      (venuesProp || []).map((v) => ({
        venue_id: v.venue_id || v.venueId,
        name: v.name || v.venue_id || v.venueId,
      })),
    [venuesProp]
  )

  const [uarCatalog, setUarCatalog] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [selectedVenue, setSelectedVenue] = useState('')

  const fetchCatalog = useCallback(async () => {
    if (!groupId) return
    setLoading(true)
    setMessage(null)
    try {
      const qs = selectedVenue ? `?venue_id=${encodeURIComponent(selectedVenue)}` : ''
      const res = await authFetch(`${apiBase}/uar-catalog${qs}`)
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setUarCatalog(json.data?.uarCatalog || [])
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load UAR catalog: ' + err.message })
      setUarCatalog([])
    } finally {
      setLoading(false)
    }
  }, [apiBase, groupId, selectedVenue])

  useEffect(() => {
    fetchCatalog()
  }, [fetchCatalog])

  const saveUarCatalog = async () => {
    setSaving(true)
    setMessage(null)
    try {
      const res = await authFetch(`${apiBase}/uar-catalog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uarCatalog,
          scope: selectedVenue ? 'venue' : 'group',
          venue_id: selectedVenue || null,
        }),
      })
      const json = await res.json()
      if (json.success) {
        setMessage({ type: 'success', text: 'UAR catalog saved' })
        await fetchCatalog()
      } else {
        setMessage({ type: 'error', text: json.error || 'Save failed' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Save failed: ' + err.message })
    } finally {
      setSaving(false)
    }
  }

  if (!groupId) return null

  if (loading) {
    return <p className="text-sm text-gray-500 py-8">Loading UAR catalog…</p>
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-teal-700" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Unusual Activity Report (UAR)</h2>
              <p className="text-sm text-gray-500">
                Report categories and options shown on floor tablets — staff pick type, option, then optional remarks
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedVenue}
              onChange={(e) => setSelectedVenue(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">Group defaults (all venues)</option>
              {venues.map((v) => (
                <option key={v.venue_id} value={v.venue_id}>
                  {v.name} ({v.venue_id})
                </option>
              ))}
            </select>
            <button type="button" onClick={fetchCatalog} className="p-2 border border-gray-300 rounded-md hover:bg-gray-50">
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {message && (
        <div
          className={`rounded-lg p-3 flex items-center ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
          ) : null}
          <span className={`text-sm ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>{message.text}</span>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <UarCatalogEditor catalog={uarCatalog} onChange={setUarCatalog} />
        <div className="flex justify-end pt-4 mt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={saveUarCatalog}
            disabled={saving || uarCatalog.length === 0}
            className="px-4 py-2 bg-teal-700 text-white text-sm font-medium rounded-md hover:bg-teal-800 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save UAR catalog'}
          </button>
        </div>
      </div>
    </div>
  )
}
